/**
 * Centralized currency handling for all supported cities.
 * All internal values are stored in USD; conversion happens at display time.
 */

import { getCityCountry } from "./areaUnit";

export interface CurrencyInfo {
  symbol: string;
  code: string;
  /** Approximate exchange rate: 1 USD = X local units */
  rateFromUSD: number;
}

const COUNTRY_CURRENCY: Record<string, CurrencyInfo> = {
  US: { symbol: "$", code: "USD", rateFromUSD: 1 },
  UK: { symbol: "£", code: "GBP", rateFromUSD: 0.79 },
  ES: { symbol: "€", code: "EUR", rateFromUSD: 0.92 },
  IT: { symbol: "€", code: "EUR", rateFromUSD: 0.92 },
  FR: { symbol: "€", code: "EUR", rateFromUSD: 0.92 },
  DE: { symbol: "€", code: "EUR", rateFromUSD: 0.92 },
  PT: { symbol: "€", code: "EUR", rateFromUSD: 0.92 },
  NL: { symbol: "€", code: "EUR", rateFromUSD: 0.92 },
  BE: { symbol: "€", code: "EUR", rateFromUSD: 0.92 },
  AT: { symbol: "€", code: "EUR", rateFromUSD: 0.92 },
  CA: { symbol: "C$", code: "CAD", rateFromUSD: 1.36 },
  AU: { symbol: "A$", code: "AUD", rateFromUSD: 1.53 },
  AE: { symbol: "د.إ", code: "AED", rateFromUSD: 3.67 },
  SG: { symbol: "S$", code: "SGD", rateFromUSD: 1.34 },
  BR: { symbol: "R$", code: "BRL", rateFromUSD: 4.97 },
  JP: { symbol: "¥", code: "JPY", rateFromUSD: 156 },
  MX: { symbol: "$", code: "MXN", rateFromUSD: 17.2 },
  CH: { symbol: "CHF ", code: "CHF", rateFromUSD: 0.88 },
};

const DEFAULT_CURRENCY: CurrencyInfo = COUNTRY_CURRENCY.US;

/**
 * Map full country names (as stored in onboarding.country) and common
 * 2-letter codes to our internal country code keys. This lets currency
 * resolution work directly from a country selection — independent of the
 * city — so users get the right symbol the moment they pick a country.
 */
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  // Full names from LOCATION_DATA
  "united states": "US",
  "usa": "US",
  "united kingdom": "UK",
  "uk": "UK",
  "great britain": "UK",
  "spain": "ES",
  "italy": "IT",
  "france": "FR",
  "germany": "DE",
  "portugal": "PT",
  "netherlands": "NL",
  "belgium": "BE",
  "austria": "AT",
  "canada": "CA",
  "australia": "AU",
  "united arab emirates": "AE",
  "uae": "AE",
  "singapore": "SG",
  "brazil": "BR",
  "japan": "JP",
  "mexico": "MX",
  "switzerland": "CH",
};

/**
 * Resolve a country code from any of: a country full name, a 2-letter code,
 * or a city name. Returns "US" as the safe default.
 */
function resolveCountryCode(input: string): string {
  const key = input.toLowerCase().trim();
  if (!key) return "US";
  // Direct 2-letter code match (e.g. "US", "DE")
  if (COUNTRY_CURRENCY[input.toUpperCase().trim()]) {
    return input.toUpperCase().trim();
  }
  // Full country name match
  if (COUNTRY_NAME_TO_CODE[key]) return COUNTRY_NAME_TO_CODE[key];
  // Fall back to city → country lookup
  return getCityCountry(input);
}

/**
 * Get full currency info for a city, country name, or country code.
 * Accepts:
 *   - City name (e.g. "Miami", "London")
 *   - Full country name (e.g. "United States", "Germany")
 *   - 2-letter country code (e.g. "US", "DE")
 */
export function getCurrencyForCity(city: string): CurrencyInfo {
  const country = resolveCountryCode(city);
  return COUNTRY_CURRENCY[country] || DEFAULT_CURRENCY;
}

/**
 * Convert a USD amount to the city's local currency.
 */
export function convertFromUSD(usdAmount: number, city: string): number {
  const { rateFromUSD } = getCurrencyForCity(city);
  return Math.round(usdAmount * rateFromUSD);
}

/**
 * Format a number with the correct currency symbol for a city.
 * Does NOT convert — assumes the value is already in local currency.
 */
export function formatCurrency(amount: number, city: string): string {
  const { symbol } = getCurrencyForCity(city);
  return `${symbol}${Math.round(amount).toLocaleString()}`;
}

/**
 * Format a number with conversion from USD to local currency.
 */
export function formatCurrencyFromUSD(usdAmount: number, city: string): string {
  const local = convertFromUSD(usdAmount, city);
  return formatCurrency(local, city);
}

/**
 * Format a range like "$150K–$350K" in local currency.
 */
export function formatCurrencyRange(lowUSD: number, highUSD: number, city: string): string {
  const { symbol } = getCurrencyForCity(city);
  const low = convertFromUSD(lowUSD, city);
  const high = convertFromUSD(highUSD, city);
  return `${symbol}${Math.round(low).toLocaleString()} – ${symbol}${Math.round(high).toLocaleString()}`;
}

/**
 * Compact format: $150K
 */
export function formatCurrencyCompact(amount: number, city: string): string {
  const { symbol } = getCurrencyForCity(city);
  if (amount >= 1000) return `${symbol}${(amount / 1000).toFixed(0)}K`;
  return `${symbol}${amount}`;
}

/**
 * Smart compact number: 1.2M, 706K, or 950 — for raw numbers (no symbol).
 * Rules:
 *  - >= 1,000,000  → "1.2M"
 *  - >= 10,000     → "706K"
 *  - >= 1,000      → "1,234" (with thousand separators)
 *  - < 1,000       → "950"
 */
export function formatCompactNumber(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) {
    const v = abs / 1_000_000;
    return `${sign}${v >= 10 ? v.toFixed(0) : v.toFixed(1)}M`;
  }
  if (abs >= 10_000) {
    return `${sign}${Math.round(abs / 1000).toLocaleString()}K`;
  }
  return `${sign}${Math.round(abs).toLocaleString()}`;
}

/**
 * Smart compact currency: "$1.2M", "€706K", "A$950".
 * Assumes amount is already in local currency.
 */
export function formatCompactCurrency(amount: number, city: string): string {
  const { symbol } = getCurrencyForCity(city);
  return `${symbol}${formatCompactNumber(amount)}`;
}

/**
 * Smart compact currency from a USD value (converts then compacts).
 */
export function formatCompactCurrencyFromUSD(usdAmount: number, city: string): string {
  return formatCompactCurrency(convertFromUSD(usdAmount, city), city);
}

/**
 * Format a percentage with at most 1 decimal place. Returns "31.9%" or "0%".
 */
export function formatPercent(value: number, opts?: { decimals?: 0 | 1 }): string {
  if (!isFinite(value)) return "—";
  const decimals = opts?.decimals ?? 1;
  const rounded = Number(value.toFixed(decimals));
  // Drop trailing ".0" for cleaner display
  const out = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(decimals);
  return `${out}%`;
}

/**
 * Get just the currency symbol for a city.
 */
export function getCurrencySymbol(city: string): string {
  return getCurrencyForCity(city).symbol;
}

/**
 * Budget range options adapted to city currency.
 */
export function getBudgetOptionsForCity(city: string) {
  const { symbol, rateFromUSD } = getCurrencyForCity(city);
  const ranges = [
    { id: "under-50k", usd: 50000 },
    { id: "50k-100k", usd: 100000 },
    { id: "100k-250k", usd: 250000 },
    { id: "250k-500k", usd: 500000 },
    { id: "500k-plus", usd: 750000 },
  ];
  return ranges.map((r, i) => {
    const local = Math.round(r.usd * rateFromUSD);
    const localK = Math.round(local / 1000);
    let label: string;
    if (i === 0) label = `Under ${symbol}${localK}K`;
    else if (i === ranges.length - 1) label = `${symbol}${localK}K+`;
    else {
      const prevLocal = Math.round(ranges[i - 1].usd * rateFromUSD / 1000);
      label = `${symbol}${prevLocal}K – ${symbol}${localK}K`;
    }
    return { id: r.id, label, value: r.usd };
  });
}
